import * as React from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'vai-fixed vai-inset-0 vai-z-[9999] vai-bg-black/80 vai- data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0',
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    hideClose?: boolean;
    overlayClassName?: string;
    closeClassName?: string;
  }
>(
  (
    { className, children, overlayClassName, closeClassName, ...props },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'vai-fixed vai-left-[50%] vai-top-[50%] vai-z-[9999] vai-grid vai-w-full vai-translate-x-[-50%] vai-translate-y-[-50%] vai-gap-4 vai-border vai-bg-background vai-p-6 vai-shadow-lg vai-duration-200 data-[state=open]:vai-animate-in data-[state=closed]:vai-animate-out data-[state=closed]:vai-fade-out-0 data-[state=open]:vai-fade-in-0 data-[state=closed]:vai-zoom-out-95 data-[state=open]:vai-zoom-in-95 data-[state=closed]:vai-slide-out-to-left-1/2 data-[state=closed]:vai-slide-out-to-top-[48%] data-[state=open]:vai-slide-in-from-left-1/2 data-[state=open]:vai-slide-in-from-top-[48%] sm:vai-rounded-lg',
          className,
        )}
        {...props}
      >
        {children}
        {!props.hideClose && (
          <DialogPrimitive.Close
            className={cn(
              'vai-absolute vai-right-4 vai-top-4 vai-rounded-sm vai-opacity-70 vai-ring-offset-background vai-transition-opacity hover:vai-opacity-100 focus:vai-outline-none focus:vai-ring-2 focus:vai-ring-ring focus:vai-ring-offset-2 disabled:vai-pointer-events-none data-[state=open]:vai-bg-accent data-[state=open]:vai-text-muted-foreground',
              closeClassName,
            )}
          >
            <X className="vai-h-4 vai-w-4" />
            <span className="vai-sr-only" data-testid="close-dialog-button">
            Close
          </span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'vai-flex vai-flex-col vai-space-y-1.5 vai-text-center sm:vai-text-left',
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'vai-flex vai-flex-col-reverse sm:vai-flex-row sm:vai-justify-end sm:vai-space-x-2',
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'vai-text-lg vai-font-semibold vai-leading-none vai-tracking-tight',
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('vai-text-sm vai-text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
